import React, { useState } from "react";
import "../styles/landingPage.css";
import { Text, TokenProvider, Button, Icon, InputFieldV2, ActionButton } from "../custom-components";
import { IcClose, IcLanguage } from "../custom-components/Icon";
import {
  BsShieldCheck,
  BsCookie,
  BsChatLeftText,
  BsClockHistory,
  BsBoxes,
  BsArrowCounterclockwise,
  BsCheck2Circle,
} from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { enableSandboxMode } from "../utils/sandboxMode";
import { toast } from "react-toastify";
import CustomToast from "./CustomToastContainer";

const heroIllustration = new URL("../assets/heroIllustration.svg", import.meta.url).href;
const privuLogo = new URL("../assets/privu-logo.png", import.meta.url).href;

const CONTACT_URL = "https://www.privu.tech/contact-us/";

const LandingPage = () => {
  const navigate = useNavigate();
  const [showSandboxModal, setShowSandboxModal] = useState(false);
  const [sandboxName, setSandboxName] = useState("");
  const [sandboxEmail, setSandboxEmail] = useState("");
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  
  // Helper to check if form is valid
  const isFormValid = () => {
    const nameValidation = validateName(sandboxName);
    const emailValidation = validateEmail(sandboxEmail);
    return nameValidation.isValid && emailValidation.isValid;
  };

  const handleClick = () => {
    navigate("/signup");
  };

  const handleContact = () => {
    window.open(CONTACT_URL, "_blank", "noopener,noreferrer");
  };

  // Input sanitization function to prevent XSS
  const sanitizeInput = (input) => {
    if (!input) return "";
    
    // Remove any HTML tags
    let sanitized = input.replace(/<[^>]*>/g, '');
    
    // Remove script-related content
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
    
    // Remove special characters that could be used for injection
    sanitized = sanitized.replace(/[<>\"'`]/g, '');
    
    return sanitized.trim();
  };

  // Validate name - only letters, spaces, hyphens, and apostrophes
  const validateName = (name) => {
    const sanitized = sanitizeInput(name);
    
    if (!sanitized) {
      return { isValid: false, error: "Name is required", sanitized: "" };
    }
    
    if (sanitized.length < 2) {
      return { isValid: false, error: "Name must be at least 2 characters", sanitized };
    }
    
    if (sanitized.length > 50) {
      return { isValid: false, error: "Name must not exceed 50 characters", sanitized };
    }
    
    // Allow only letters, spaces, hyphens, apostrophes, and dots
    const namePattern = /^[a-zA-Z][a-zA-Z\s\-'\.]*$/;
    if (!namePattern.test(sanitized)) {
      return { isValid: false, error: "Name can only contain letters, spaces, hyphens, and apostrophes", sanitized };
    }
    
    // Check for suspicious patterns
    const suspiciousPatterns = /(\bscript\b|\balert\b|\beval\b|\bdocument\b|\bwindow\b)/i;
    if (suspiciousPatterns.test(sanitized)) {
      return { isValid: false, error: "Invalid characters detected", sanitized };
    }
    
    return { isValid: true, error: "", sanitized };
  };

  // Validate email with comprehensive checks
  const validateEmail = (email) => {
    const sanitized = sanitizeInput(email);
    
    if (!sanitized || sanitized.trim() === '') {
      return { isValid: false, error: "Email is required", sanitized: "" };
    }
    
    if (sanitized.length > 100) {
      return { isValid: false, error: "Email must not exceed 100 characters", sanitized };
    }
    
    // RFC 5322 compliant email regex (simplified but strict)
    const emailPattern = /^[a-zA-Z0-9]([a-zA-Z0-9._-])*@[a-zA-Z0-9]([a-zA-Z0-9.-])*\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(sanitized)) {
      return { isValid: false, error: "Please enter a valid email address", sanitized };
    }
    
    // Additional checks for suspicious patterns
    const suspiciousPatterns = /(\bscript\b|\balert\b|\beval\b|\bdocument\b|\bwindow\b|javascript:|data:)/i;
    if (suspiciousPatterns.test(sanitized)) {
      return { isValid: false, error: "Invalid email format", sanitized };
    }
    
    // Check for multiple @ symbols
    if ((sanitized.match(/@/g) || []).length !== 1) {
      return { isValid: false, error: "Invalid email format", sanitized };
    }
    
    // Ensure there's text before and after @
    const parts = sanitized.split('@');
    if (parts.length !== 2 || !parts[0] || !parts[1] || !parts[1].includes('.')) {
      return { isValid: false, error: "Please enter a valid email address", sanitized };
    }
    
    return { isValid: true, error: "", sanitized };
  };

  // Handle name input with validation
  const handleNameChange = (e) => {
    const value = e.target.value;
    const validation = validateName(value);
    
    setSandboxName(validation.sanitized);
    setNameError(validation.error);
  };

  // Handle email input with validation
  const handleEmailChange = (e) => {
    const value = e.target.value;
    const validation = validateEmail(value);
    
    setSandboxEmail(validation.sanitized);
    setEmailError(validation.error);
    
    // Clear error if email becomes valid
    if (validation.isValid && emailError) {
      setEmailError("");
    }
  };

const handleSandboxEntry = () => {
  // Validate name
  const nameValidation = validateName(sandboxName);
  if (!nameValidation.isValid) {
    setNameError(nameValidation.error);
    toast.error(
      (props) => (
        <CustomToast
          {...props}
          type="error"
          message={nameValidation.error || "Please enter a valid name"}
        />
      ),
      { icon: false }
    );
    return;
  }
  
  // Validate email
  const emailValidation = validateEmail(sandboxEmail);
  if (!emailValidation.isValid) {
    setEmailError(emailValidation.error);
    toast.error(
      (props) => (
        <CustomToast
          {...props}
          type="error"
          message={emailValidation.error || "Please enter a valid email address"}
        />
      ),
      { icon: false }
    );
    return;
  }
  
  // Clear any previous errors
  setNameError("");
  setEmailError("");
  
  // Enable sandbox mode with sanitized user data
  enableSandboxMode({ 
    name: nameValidation.sanitized, 
    email: emailValidation.sanitized 
  });
  
  // Open sandbox in new tab
  window.open('/dpo-dashboard?sandbox=true', '_blank');
  
  // Close modal and reset
  setShowSandboxModal(false);
  setSandboxName("");
  setSandboxEmail("");
  setNameError("");
  setEmailError("");
};

  return (
    <TokenProvider
          value={{
            theme: "PrivuBase",
            mode: "light",
          }}
        >
    <div className="landing-page">

      {/* Hero Section */}
        <section className="hero-container">
            <div className="hero-left">
                <span className="hero-badge">
                    <span className="hero-badge-dot" />
                    Built for India&rsquo;s DPDP Act, 2023
                </span>
                <div className="main-heading">
                <Text appearance='heading-m' color='primary_grey_100'>
                Manage
                </Text>
                <Text appearance='heading-m' color='primary_50'>
                 consent
                </Text>
                <Text appearance='heading-m' color='primary_grey_100'>
                 without
                </Text>
                <Text appearance='heading-m' color='primary_grey_100'>
                 any hassle.
                </Text>
                </div>

                <div className="hero-subs">
                    <Text appearance='body-m' color='primary_grey_80'>
                    Stay compliant with India&rsquo;s DPDP Act, 2023 and give users control over their data — all in one secure platform.
                    </Text>
                </div>
                <div className="hero-buttons">
                <Button
                    ariaControls="Button Clickable"
                    ariaDescribedby="Button"
                    ariaExpanded="Expanded"
                    ariaLabel="Button"
                    className="Button"
                    icon=""
                    iconAriaLabel="Icon Favorite"
                    iconLeft=""
                    kind="primary"
                    label="Get started"
                    onClick={handleClick}
                    size="large"
                    state="normal"
                    />
                <Button
                    ariaControls="Contact sales"
                    ariaDescribedby="Button"
                    ariaExpanded="Expanded"
                    ariaLabel="Contact sales"
                    className="Button"
                    kind="secondary"
                    label="Talk to sales"
                    onClick={handleContact}
                    size="large"
                    state="normal"
                    />
                </div>

            </div>

            <div className="hero-right">
                <img
                    src={heroIllustration}
                    alt="Consent management dashboard with a compliance shield and a resting user avatar"
                    className="hero-image"
                />
            </div>
        </section>



          {/* Capability Cards */}
      <section className="feature-cards">
        <div className="section-head">
          <span className="section-eyebrow">One stop solution</span>
          <Text appearance='heading-s' color='primary_grey_100'>
            We have everything you need
          </Text>
          <p className="section-sub">
            The controls compliance teams reach for every day, in one place.
          </p>
        </div>
        <div className="cards">

          <div className="card-landing">
              <span className="card-icon">
                <Icon ic={<BsClockHistory />} color="primary_60" size={22} />
              </span>
              <Text appearance='heading-xs' color='primary_grey_100'>
                Secure audit logs
              </Text>
              <p className="card-copy">
                Always be ready for compliance checks.
              </p>
          </div>

          <div className="card-landing">
              <span className="card-icon">
                <Icon ic={<IcLanguage />} color="primary_60" size={22} />
              </span>
              <Text appearance='heading-xs' color='primary_grey_100'>
                Multi-language support
              </Text>
              <p className="card-copy">
                Communicate in your users&rsquo; preferred language.
              </p>
          </div>

          <div className="card-landing">
              <span className="card-icon">
                <Icon ic={<BsBoxes />} color="primary_60" size={22} />
              </span>
              <Text appearance='heading-xs' color='primary_grey_100'>
                Cross-platform integration
              </Text>
              <p className="card-copy">
                Works across websites, apps, and digital touchpoints.
              </p>
          </div>

          <div className="card-landing">
              <span className="card-icon">
                <Icon ic={<BsArrowCounterclockwise />} color="primary_60" size={22} />
              </span>
              <Text appearance='heading-xs' color='primary_grey_100'>
                Consent withdrawal anytime
              </Text>
              <p className="card-copy">
                Let users change their mind in seconds.
              </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
  <div className="section-head">
    <span className="section-eyebrow">The platform</span>
    <Text appearance="heading-s" color="primary_grey_100">
      Why Privu Consent Management is right fit for you
    </Text>
    <p className="section-sub">
      Three capabilities that cover the full consent lifecycle.
    </p>
  </div>

  <div className="features-grid">

    <div className="feature-card">
      <div className="feature-card-top">
        <span className="feature-card-icon">
          <Icon ic={<BsShieldCheck />} color="primary_60" size={24} />
        </span>
        <span className="feature-card-num">01</span>
      </div>
      <Text appearance="heading-xs" color="primary_grey_100">
        Consent management
      </Text>
      <ul className="feature-points">
        <li>Deploy banners, forms, and pop-ups — no coding required.</li>
        <li>Let users give, withdraw, or modify consent anytime.</li>
        <li>Keep time-stamped, tamper-proof consent records.</li>
        <li>Multi-language support to engage users in their preferred language.</li>
      </ul>
    </div>

    <div className="feature-card">
      <div className="feature-card-top">
        <span className="feature-card-icon">
          <Icon ic={<BsCookie />} color="primary_60" size={24} />
        </span>
        <span className="feature-card-num">02</span>
      </div>
      <Text appearance="heading-xs" color="primary_grey_100">
        Cookie consent
      </Text>
      <ul className="feature-points">
        <li>Configure and display customizable cookie banners.</li>
        <li>Let users choose their tracking preferences simply.</li>
        <li>Automatically block non-essential cookies until consent is given.</li>
        <li>Cross-platform support: websites, mobile apps, and kiosks.</li>
      </ul>
    </div>

    <div className="feature-card">
      <div className="feature-card-top">
        <span className="feature-card-icon">
          <Icon ic={<BsChatLeftText />} color="primary_60" size={24} />
        </span>
        <span className="feature-card-num">03</span>
      </div>
      <Text appearance="heading-xs" color="primary_grey_100">
        Grievance redressal
      </Text>
      <ul className="feature-points">
        <li>Let Data Principals raise grievances securely.</li>
        <li>Provide a dedicated grievance portal with status tracking.</li>
        <li>Automate response workflows to save time and ensure SLA compliance.</li>
        <li>Generate reports for audits or internal reviews instantly.</li>
      </ul>
    </div>

  </div>
</section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="final-cta-inner">
          <Text appearance="heading-s" color='primary-inverse'>
            Ready to get started?
          </Text>
          <p className="final-cta-sub">
            Set up your consent workspace in minutes.
          </p>
          <div className="cta-buttons">
              <Button
                ariaLabel="Get started"
                kind="secondary"
                label="Get started"
                onClick={handleClick}
                size="large"
                state="normal"
              />
              <Button
                ariaLabel="Contact sales"
                kind="tertiary"
                label="Talk to sales"
                onClick={handleContact}
                size="large"
                state="normal"
              />
          </div>
        </div>
      </section>

      {/* Site Footer */}
      <footer className="lp-footer" id="contact">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <img src={privuLogo} alt="PrivuTech" className="lp-footer-logo" />
            <p className="lp-footer-blurb">
              Consent management built for India&rsquo;s DPDP Act, 2023.
            </p>
          </div>

          <nav className="lp-footer-links" aria-label="Footer">
            <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer">
              Contact Us
            </a>
            <a href="https://www.privu.tech/privacy-policy/" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            <a href="https://www.privu.tech/terms-of-service/" target="_blank" rel="noopener noreferrer">
              Terms &amp; Conditions
            </a>
          </nav>
        </div>

        <div className="lp-footer-base">
          © 2026 PrivuTech (Taxology). All rights reserved.
        </div>
      </footer>


      {/* Sandbox Entry Modal */}
      {showSandboxModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "30px",
            maxWidth: "450px",
            width: "90%",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)"
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px"
            }}>
              <Text appearance="heading-xs" color="primary-grey-100">
                Try Sandbox Mode
              </Text>
              <ActionButton
                onClick={() => setShowSandboxModal(false)}
                icon={<IcClose />}
                kind="tertiary"
              />
            </div>

            <Text appearance="body-s" color="primary-grey-80" style={{ display: "block", marginBottom: "20px" }}>
              Enter your details to explore our Consent Management System with dummy data. No real account needed!
            </Text>

            <div style={{ marginBottom: "15px" }}>
              <InputFieldV2
                label="Your Name"
                value={sandboxName}
                placeholder="Enter your name (e.g., John Doe)"
                size="medium"
                onChange={handleNameChange}
                maxLength={50}
                required
              />
              {nameError && (
                <Text 
                  appearance="body-xxs" 
                  color="primary-red-100" 
                  style={{ display: "block", marginTop: "4px" }}
                >
                  {nameError}
                </Text>
              )}
            </div>

            <div style={{ marginBottom: "25px" }}>
              <InputFieldV2
                label="Your Email"
                value={sandboxEmail}
                placeholder="Enter your email (e.g., john@example.com)"
                size="medium"
                type="email"
                onChange={handleEmailChange}
                maxLength={100}
                required
              />
              {emailError && (
                <Text 
                  appearance="body-xxs" 
                  color="primary-red-100" 
                  style={{ display: "block", marginTop: "4px" }}
                >
                  {emailError}
                </Text>
              )}
            </div>

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <Button
                kind="secondary"
                label="Cancel"
                onClick={() => {
                  setShowSandboxModal(false);
                  setSandboxName("");
                  setSandboxEmail("");
                  setNameError("");
                  setEmailError("");
                }}
                size="medium"
              />
              <Button
                kind="primary"
                label="Launch Sandbox"
                onClick={handleSandboxEntry}
                size="medium"
                disabled={!isFormValid()}
              />
            </div>

            <div style={{
              marginTop: "20px",
              padding: "12px",
              backgroundColor: "#F0F7FF",
              borderRadius: "8px"
            }}>
              <Text appearance="body-xxs" color="primary-grey-80" style={{ display: "block" }}>
                ℹ️ Sandbox mode uses test data only. No actual data is saved or processed.
              </Text>
            </div>
          </div>
        </div>
      )}
 
    </div>
    </TokenProvider>
  );
};

export default LandingPage;
